import { Component, input } from "@angular/core";
import { AbstractControl } from "@angular/forms";
import { InputErrorComponent } from "../input-error/input-error.component";
import { CharacterCountComponent } from "../character-count/character-count.component";

/**
 * Standardized form field wrapper.
 *
 * Owns the label, required/optional marker, invalid styling and error text so
 * parent forms no longer repeat `.form-field`/`.field-label`/`app-input-error`
 * markup or `isInvalid()` bindings. Pass the `control` and let the field derive
 * its own validity, or drive it manually with the `invalid` input.
 *
 * The actual control (p-select, textarea, app-select-field, ...) is projected.
 */
@Component({
  selector: "app-form-field",
  standalone: true,
  imports: [InputErrorComponent, CharacterCountComponent],
  templateUrl: "./form-field.component.html",
  styleUrl: "./form-field.component.scss"
})
export class FormFieldComponent {
  /** Text rendered in the `<label>`. */
  label = input<string>("");
  /** Wires `<label for>` to the projected control for accessibility. */
  inputId = input<string>("");
  /** Renders a trailing `*` on the label. */
  required = input<boolean>(false);
  /** Renders a trailing `(optional)` marker on the label. */
  optional = input<boolean>(false);
  /** Extra muted text rendered after the label and required/optional marker. */
  labelSuffix = input<string>("");
  /** Optional helper text shown in the footer. */
  hint = input<string>("");
  /** The reactive control; used to derive invalid state and the error message. */
  control = input<AbstractControl | null>(null);
  /** Maps a control error key (e.g. `required`) to the message to display. */
  errorMessages = input<Record<string, string>>({});
  /** Force the invalid state without a control (e.g. custom validation). */
  invalid = input<boolean>(false);
  /** When set, renders a right-aligned character counter in the footer for the control's value length. */
  maxLength = input<number | null>(null);
  /** Overrides the test id; when omitted one is derived from the label. */
  testId = input<string>("");

  /** Always resolves to a usable hook: the explicit `testId`, else a slug of the label. */
  protected get resolvedTestId(): string {
    const explicit = this.testId().trim();
    if (explicit) {
      return explicit;
    }
    const slug = this.label().toLowerCase()
      .replace(/[^a-z0-9]+/g, "-")
      .replace(/^-|-$/g, "");
    return slug ? `form-field-${slug}` : "app-form-field";
  }

  protected get showError(): boolean {
    if (this.invalid()) {
      return true;
    }
    const control = this.control();
    return !!control && control.invalid && (control.dirty || control.touched);
  }

  protected get errorText(): string | null {
    const control = this.control();
    const messages = this.errorMessages();
    if (!control?.errors) {
      return null;
    }
    const matchingKey = Object.keys(control.errors).find(key => key in messages);
    return matchingKey ? messages[matchingKey] : null;
  }

  protected get controlLength(): number {
    const value = this.control()?.value;
    return typeof value === "string" ? value.length : 0;
  }
}
